/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { ActionFail, ActionRequest, ActionSuccess, Channel, XError } from '@wit/vtv-x-foundation-lib';
import { getChannelFromStorage, Selectors as ChannelsSelectors } from '../../../core/store/ducks/epg/channels';
import { StoreState } from '../../store';

// ACTIONS
export enum ActionType {
  INIT = 'watchTv/CURRENT_CHANNEL_INIT',
  SUCCESS = 'watchTv/CURRENT_CHANNEL_SUCCESS',
  FAIL = 'watchTv/CURRENT_CHANNEL_FAIL',
}

interface Payload {
  currentChannel?: Channel;
}

type Init = ActionRequest<ActionType.INIT>;
type Success = ActionSuccess<ActionType.SUCCESS, Payload>;
type Fail = ActionFail<ActionType.FAIL>;

type Actions = Init | Success | Fail;

const Actions = {
  init: (): Init => ({ type: ActionType.INIT }),
  success: (currentChannel?: Channel): Success => ({
    type: ActionType.SUCCESS,
    payload: { currentChannel },
  }),
  fail: (error: XError): Fail => ({
    type: ActionType.FAIL,
    error,
  }),
};

export const ActionCreators = {
  init: Actions.init,
};

//
// REDUCER
//

type ReducerState = {
  channel?: Channel;
  processing: boolean;
  processed: boolean;
  error?: XError;
};

const initialState: ReducerState = {
  processing: false,
  processed: false,
};

export default function reducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.INIT:
      return {
        ...state,
        processing: false,
        processed: true,
        error: undefined,
      };
    case ActionType.SUCCESS:
      return {
        ...state,
        channel: action.payload.currentChannel,
        processing: false,
        processed: true,
      };
    case ActionType.FAIL:
      return {
        processing: false,
        processed: true,
        error: action.error,
      };

    default:
      return state;
  }
}

//
// SAGAS
//

// private sagas

// Current tuned channel
function* getTunedChannel(): SagaIterator<Channel | undefined> {
  const currentChanel: Channel | undefined = yield call(() => undefined); // TODO: save tuned channel when entering channel page and read it here
  return currentChanel;
}

// Stored channel
function* getStoredChannel(): SagaIterator<Channel | undefined> {
  const channelEpgId: number | undefined = yield call(getChannelFromStorage);
  if (channelEpgId) {
    const storedChanel: Channel | undefined = yield select(ChannelsSelectors.getChannelByEpgId, channelEpgId);
    return storedChanel;
  }
  return undefined;
}

// First epg channel the user is entitled
function* getFirstChannel(): SagaIterator<Channel | undefined> {
  const channels: Channel[] | undefined = yield select(ChannelsSelectors.getChannels);
  if (channels?.length) {
    const firstChannel: Channel | undefined = channels.find((channel) => channel.isSubscribed);
    if (firstChannel) {
      return firstChannel;
    }
    // if the user is not entitled to any channel return the first of the list
    return channels[0];
  }
  return undefined;
}

// Current channel
export function* getCurrentChannel(): SagaIterator<Channel | undefined> {
  let currentChannel: Channel | undefined = yield call(getTunedChannel);

  // If there is no tunned channel is because the channel is not tuned yet, get it from the store
  if (!currentChannel) {
    currentChannel = yield call(getStoredChannel);
  }

  // If there is no channel in the store is because this is the first time the app is running, get the first channel of the list
  if (!currentChannel) {
    return undefined;
  }

  return currentChannel;
}

function* initWatchTvData(): SagaIterator {
  try {
    const currentChannel: Channel | undefined = yield call(getCurrentChannel);
    yield put(Actions.success(currentChannel));
  } catch (e) {
    yield put(Actions.fail(XError.fromError(e)));
  }
}

// public sagas
export function* saga(): SagaIterator {
  yield all([takeLatest(ActionType.INIT, initWatchTvData)]);
}

//
// SELECTORS
//

export const Selectors = {
  processed: (state: StoreState): boolean | undefined => state.watchTv.currentChannel?.processed,
  error: (state: StoreState): XError | undefined => state.watchTv.currentChannel?.error,
  currentChannel: (state: StoreState): Channel | undefined => state.watchTv.currentChannel?.channel,
};
